/**
 * Suite — a named collection of Tasks measuring a capability or guarding
 * against regression. A Task can belong to several suites simultaneously.
 *
 * Two kinds:
 *   - capability: pass rate is low or climbing. Measuring new abilities.
 *   - regression: pass rate should be ~100%. Catching regressions.
 *
 * "Graduation" from capability → regression = adding a suite membership.
 * Non-destructive — the Task itself doesn't change, it just gains a new
 * suite association.
 *
 * Regression suites with gatesRun=true fail the entire eval run if any
 * Task's pass rate drops below the expected baseline.
 */
import { z } from 'zod';
import { EvaluationTargetKindSchema } from './eval.js';
// ─── SuiteTask (join) ───────────────────────────────────────────────────────
export const SuiteTaskSchema = z.object({
    id: z.string(),
    suiteId: z.string(),
    taskId: z.string(),
    createdAt: z.string().datetime(),
});
// ─── Suite ──────────────────────────────────────────────────────────────────
export const SuiteKindSchema = z.enum(['CAPABILITY', 'REGRESSION']);
export const SuiteSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    /** Free-text purpose/scope description. Consumed by the refinement agent
     *  to understand what the suite measures and when to add tasks to it. */
    description: z.string().nullable(),
    /** The workflow this suite evaluates. Advisory — shown in UI for scope clarity. */
    workflowId: z.string().nullable().default(null),
    /** — restricts Task membership by target. */
    targetKind: EvaluationTargetKindSchema.default('WORKFLOW'),
    kind: SuiteKindSchema,
    /** For regression suites: expected baseline pass rate. */
    expectedPassRate: z.number().min(0).max(1).nullable(),
    /** When true, eval run fails if any Task's pass rate drops. */
    gatesRun: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.date().nullable(),
    /** Hydrated on list/get — Task memberships. */
    tasks: z.array(SuiteTaskSchema).default([]),
});
// ─── Inputs ─────────────────────────────────────────────────────────────────
export const CreateSuiteInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    workflowId: z.string().nullable().optional(),
    targetKind: EvaluationTargetKindSchema.optional(),
    kind: SuiteKindSchema.optional(),
    expectedPassRate: z.number().min(0).max(1).nullable().optional(),
    gatesRun: z.boolean().optional(),
    /** Initial task IDs to add as members. */
    taskIds: z.array(z.string()).optional(),
});
export const UpdateSuiteInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    workflowId: z.string().nullable().optional(),
    kind: SuiteKindSchema.optional(),
    expectedPassRate: z.number().min(0).max(1).nullable().optional(),
    gatesRun: z.boolean().optional(),
});
//# sourceMappingURL=suite.js.map